import { useEffect } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { CardScanner } from "@/components/features/scanner/CardScanner";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Camera, Check, Star, Award } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ScanCounter } from "@/components/features/scanner/ScanCounter";
import { useScanQuota } from "@/hooks/useScanQuota";
import { ScanStateProvider } from '@/hooks/ScanStateContext';

const ScanPage = () => {
  const isMobile = useIsMobile();
  const { refreshQuota } = useScanQuota();
  
  // Ensure we have fresh quota data when arriving on scan page
  useEffect(() => {
    console.log('ScanPage mounted - refreshing quota data');
    refreshQuota();
  }, [refreshQuota]);
  
  return (
    <ScanStateProvider>
      <PageContainer>
        <motion.div
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.h1 
            className="text-3xl font-bold mb-2"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            PokeScan Card Scanner
          </motion.h1>
          
          {/* Hide description on mobile screens */}
          {!isMobile && (
            <motion.p
              className="text-muted-foreground mb-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Our AI-powered scanner identifies your Pokémon cards and provides current market valuations
            </motion.p>
          )}
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Camera view - The primary focus */}
            <motion.div 
              className="md:col-span-2"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <CardScanner />
              
              {/* Scan count indicator - Below the camera */}
              <div className="mt-3">
                <ScanCounter />
              </div>
            </motion.div>
            
            {/* Right sidebar - How It Works and Card Value Tiers */}
            <div className="space-y-6">
              {/* How It Works - First on the right panel */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <Card className="shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <Camera className="h-5 w-5 mr-2 text-pokemon-blue" />
                      <h2 className="text-xl font-semibold">How It Works</h2>
                    </div>
                    <ol className="space-y-3 text-sm">
                      <li className="flex">
                        <div className="bg-pokemon-blue text-white rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">1</div>
                        <span>Position your Pokémon card in a well-lit area</span>
                      </li>
                      <li className="flex">
                        <div className="bg-pokemon-blue text-white rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">2</div>
                        <span>Tap the Capture Card button when ready</span>
                      </li>
                      <li className="flex">
                        <div className="bg-pokemon-blue text-white rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">3</div>
                        <span>Our AI analyzes your card</span>
                      </li>
                      <li className="flex">
                        <div className="bg-pokemon-blue text-white rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">4</div>
                        <span>View current market prices and card details instantly</span>
                      </li>
                    </ol>
                  </CardContent>
                </Card>
              </motion.div>
              
              {/* Card Value Tiers - Second on the right panel */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
              >
                <Card className="border border-pokemon-blue/10 shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-center mb-2">
                      <Award className="h-4 w-4 mr-2 text-pokemon-blue" />
                      <h2 className="text-lg font-semibold">Card Value Tiers</h2>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 rounded-lg bg-rose-50 border border-rose-200 flex items-center">
                        <div className="w-3 h-3 bg-rose-500 rounded-full mr-2 ring-1 ring-rose-200"></div>
                        <div>
                          <span className="font-medium text-rose-800">$50,000+</span>
                          <span className="block text-rose-700 text-[10px]">Ultra rare treasure</span>
                        </div>
                      </div>
                      
                      <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 flex items-center">
                        <div className="w-3 h-3 bg-amber-500 rounded-full mr-2 ring-1 ring-amber-200"></div>
                        <div>
                          <span className="font-medium text-amber-800">$1,000-50,000</span>
                          <span className="block text-amber-700 text-[10px]">Incredible find</span>
                        </div>
                      </div>
                      
                      <div className="p-2 rounded-lg bg-yellow-50 border border-yellow-200 flex items-center">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2 ring-1 ring-yellow-200"></div>
                        <div>
                          <span className="font-medium text-yellow-800">$100-1,000</span>
                          <span className="block text-yellow-700 text-[10px]">Legendary</span>
                        </div>
                      </div>
                      
                      <div className="p-2 rounded-lg bg-purple-50 border border-purple-200 flex items-center">
                        <div className="w-3 h-3 bg-purple-500 rounded-full mr-2 ring-1 ring-purple-200"></div>
                        <div>
                          <span className="font-medium text-purple-800">$50-100</span>
                          <span className="block text-purple-700 text-[10px]">Ultra rare</span>
                        </div>
                      </div>
                      
                      <div className="p-2 rounded-lg bg-blue-50 border border-blue-200 flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-2 ring-1 ring-blue-200"></div>
                        <div>
                          <span className="font-medium text-blue-800">$20-50</span>
                          <span className="block text-blue-700 text-[10px]">Rare</span>
                        </div>
                      </div>
                      
                      <div className="p-2 rounded-lg bg-green-50 border border-green-200 flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-2 ring-1 ring-green-200"></div>
                        <div>
                          <span className="font-medium text-green-800">$5-20</span>
                          <span className="block text-green-700 text-[10px]">Uncommon</span>
                        </div>
                      </div>
                      
                      <div className="p-2 rounded-lg bg-gray-50 border border-gray-200 flex items-center col-span-2">
                        <div className="w-3 h-3 bg-gray-400 rounded-full mr-2 ring-1 ring-gray-200"></div>
                        <div>
                          <span className="font-medium text-gray-800">Below $5</span>
                          <span className="block text-gray-600 text-[10px]">Common</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
          
          {/* Tips for Perfect Scans - Full width below everything */}
          <motion.div
            className="mt-6 w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
          >
            <Card className="w-full shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <Star className="h-5 w-5 mr-2 text-pokemon-blue" />
                  <h2 className="text-xl font-semibold">Tips for Perfect Scans</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-start">
                    <div className="bg-pokemon-blue/10 text-pokemon-blue rounded-full p-1 mr-2 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span><strong>Good lighting</strong> - Use even, bright light without glare or shadows</span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-pokemon-blue/10 text-pokemon-blue rounded-full p-1 mr-2 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span><strong>Clean background</strong> - Place card against a plain, contrasting background</span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-pokemon-blue/10 text-pokemon-blue rounded-full p-1 mr-2 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span><strong>Whole card visible</strong> - Ensure the entire card fits in the frame</span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-pokemon-blue/10 text-pokemon-blue rounded-full p-1 mr-2 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span><strong>Hold steady</strong> - Keep your device still when capturing</span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-pokemon-blue/10 text-pokemon-blue rounded-full p-1 mr-2 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span><strong>Avoid reflections</strong> - Tilt the card slightly if needed to reduce glare</span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-pokemon-blue/10 text-pokemon-blue rounded-full p-1 mr-2 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <span><strong>Check edges</strong> - All four edges of the card should be clearly visible</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </PageContainer>
    </ScanStateProvider>
  );
};

export default ScanPage;
